import * as React from 'react'

import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { OneDayCalendarIcon } from '@/app/features/icons/OneDayCalendar'
import { SevenDayCalendarIcon } from '@/app/features/icons/SevenDayCalendar'
import { OneMonthCalendarIcon } from '@/app/features/icons/OneMonthCalendar'
import { ChartIcon } from '@/app/features/icons/Chart'
import { faChevronDownSolid, FaIcon } from '@/lib/fa'

import { Delegation } from '@/app/types/creator'
import { CreatorGenerationButtonMode } from './delegationGeneration'
import { DelegationsDetailsTable } from './DelegationsDetailsTable'
import type { DelegationsDriverRow } from './DelegationsGroupedTable'

type Props = {
  driver: DelegationsDriverRow
  columnsCount: number
  isExpanded: boolean
  isLoading: boolean
  showSummary: boolean
  errorMessage?: string
  onToggle: () => void
  onGenerate: (mode: CreatorGenerationButtonMode) => void
  onRemoveFromSettlement?: () => void
  renderErrorAction?: (driverId: string) => React.ReactNode
  renderDetailRowActions: (
    delegation: Delegation,
    driverId: string,
  ) => React.ReactNode
}

export const DelegationsRowItem = ({
  driver,
  columnsCount,
  isExpanded,
  isLoading,
  showSummary,
  errorMessage,
  onToggle,
  onGenerate,
  onRemoveFromSettlement,
  renderErrorAction,
  renderDetailRowActions,
}: Props) => {
  const { tSafe } = useLocalize()
  const hasDelegations = !!driver.delegations?.length
  const clickable = hasDelegations && !isLoading

  const handleRowClick = () => {
    if (clickable) onToggle()
  }

  const handleGenerate = (
    e: React.MouseEvent<HTMLButtonElement>,
    mode: CreatorGenerationButtonMode,
  ) => {
    e.stopPropagation()
    onGenerate(mode)
  }

  return (
    <>
      <tr
        className={`notches-border--right tw-relative tw-w-fit tw-border-b tw-bg-white ${
          clickable ? 'tw-cursor-pointer hover:tw-bg-primary/10' : ''
        }`}
        onClick={handleRowClick}
      >
        <TableTextRow size="medium" textSize="sm" pointer={clickable}>
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <span className="tw-flex tw-items-center tw-gap-2">
              {`${driver.name} ${driver.surname || ''}`.trim()}
              {isLoading && (
                <svg
                  className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-animate-spin tw-text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="tw-opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="tw-opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
            </span>
            {!isLoading && errorMessage && (
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                <span className="tw-text-xs tw-font-medium tw-text-red-600">
                  {errorMessage}
                </span>
                <div
                  className="tw-flex tw-flex-wrap tw-items-center tw-gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderErrorAction?.(driver.driverId)}
                  {onRemoveFromSettlement && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onRemoveFromSettlement()
                      }}
                      className="tw-rounded tw-border tw-border-red-300 tw-bg-red-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-red-700 tw-transition-colors hover:tw-bg-red-100"
                    >
                      {tSafe(
                        'creator.delegations.removeFromSettlement',
                        'Usuń z rozliczenia',
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </TableTextRow>

        {showSummary && (
          <>
            <TableTextRow size="medium" textSize="sm" pointer={clickable}>
              {String(driver.summary?.totalTime ?? '')}
            </TableTextRow>
            <TableTextRow size="medium" textSize="sm" pointer={clickable}>
              {String(driver.summary?.totalAccommodation ?? '')}
            </TableTextRow>
            <TableTextRow size="medium" textSize="sm" pointer={clickable}>
              {String(driver.summary?.totalAllowances ?? '')}
            </TableTextRow>
          </>
        )}

        <TableTextRow size="medium" textSize="sm" pointer={clickable}>
          <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
            {isLoading ? (
              <span className="tw-animate-pulse tw-text-xs tw-text-content-base/70">
                {tSafe('creator.common.generating', 'Generowanie...')}
              </span>
            ) : !hasDelegations ? (
              <>
                <button
                  type="button"
                  onClick={(e) => handleGenerate(e, '1-day')}
                  className="tw-rounded-lg tw-p-1 tw-text-primary tw-transition-colors hover:tw-bg-primary/10"
                >
                  <OneDayCalendarIcon className="tw-h-4 tw-w-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => handleGenerate(e, '7-day')}
                  className="tw-rounded-lg tw-p-1 tw-text-primary tw-transition-colors hover:tw-bg-primary/10"
                >
                  <SevenDayCalendarIcon className="tw-h-4 tw-w-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => handleGenerate(e, '30-day')}
                  className="tw-rounded-lg tw-p-1 tw-text-primary tw-transition-colors hover:tw-bg-primary/10"
                >
                  <OneMonthCalendarIcon className="tw-h-4 tw-w-4" />
                </button>
              </>
            ) : null}
            {!isLoading && (
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                className="tw-rounded-lg tw-p-1 tw-text-focus-300 tw-transition-colors hover:tw-bg-base-200"
              >
                <ChartIcon className="tw-h-4 tw-w-4" />
              </button>
            )}
            {hasDelegations && (
              <div
                className={`tw-rounded-lg tw-bg-secondary tw-p-1 ${
                  isExpanded ? 'tw-rotate-180' : ''
                } tw-transition-transform`}
              >
                <FaIcon className="tw-h-4 tw-w-4" icon={faChevronDownSolid} />
              </div>
            )}
          </div>
        </TableTextRow>
      </tr>

      {isExpanded && hasDelegations && (
        <tr className="tw-border-b tw-border-base-300">
          <td colSpan={columnsCount} className="tw-bg-[#F2F2F240] tw-py-0">
            <DelegationsDetailsTable
              delegations={driver.delegations ?? []}
              driverId={driver.driverId}
              renderRowActions={renderDetailRowActions}
            />
          </td>
        </tr>
      )}
    </>
  )
}
